import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from './msalConfig';
import { getItems, createItem, updateItem } from './fitness/fitnessApi';

const LIST_NAME = 'DinnerOrders';
const INV_LIST = 'KitchenInventory';
const PURCHASE_LIST = 'PurchaseLog';

const PURCHASE_TYPES = [
  'Ribeye Loin',
  'Ground Beef',
  'Wings',
];

const INV_ITEMS = [
  { key: 'Ribeye Loin', label: 'Ribeye Loins', unit: '', steakInfo: (n) => `(~${n * 15} steaks)` },
  { key: 'Sealed Pack', label: 'Sealed Packs', unit: '', steakInfo: (n) => `(${n * 2} steaks)` },
  { key: 'Cooked/Racked', label: 'Racked', unit: '', steakInfo: () => '(individual steaks)' },
  { key: 'Ground Beef', label: 'Ground Beef', unit: 'lbs', steakInfo: (n) => `(~${Math.floor(n / 0.75)} servings)` },
  { key: 'Wings', label: 'Wings', unit: 'lbs', steakInfo: (n) => `(~${Math.floor(n / 1.5)} servings)` },
];

const FAMILY = [
  { name: 'Marc', default: true },
  { name: 'Angela', default: true },
  { name: 'Taylor', default: true },
  { name: 'John', default: true },
  { name: 'Levi', default: true },
];

const PROTEIN_OPTIONS = ['Steak', 'Wings', 'Tacos'];

// Map old SharePoint ItemType values to current keys
const INV_TYPE_ALIASES = {
  'Whole Loin': 'Ribeye Loin',
};

const MARC_EMAIL = 'marc@defineholy.com';

function todayISO() {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

function DinnerOrder() {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [existingId, setExistingId] = useState(null);

  // Form state
  const [date, setDate] = useState(todayISO());
  const [members, setMembers] = useState(FAMILY.map(f => ({ ...f, checked: f.default, protein: 'Steak' })));
  const [guests, setGuests] = useState([]);
  const [dinnerTime, setDinnerTime] = useState('5:00 PM');
  const [headcountOverride, setHeadcountOverride] = useState(null);

  // Kitchen Inventory state
  const [inventory, setInventory] = useState(
    Object.fromEntries(INV_ITEMS.map(i => [i.key, 0]))
  );
  const [savedInventory, setSavedInventory] = useState(
    Object.fromEntries(INV_ITEMS.map(i => [i.key, 0]))
  );
  const [invIds, setInvIds] = useState({});
  const [selectedInv, setSelectedInv] = useState(null);
  const [invSaving, setInvSaving] = useState(false);
  const [invSuccess, setInvSuccess] = useState(null);

  const invDirty = INV_ITEMS.some(i => inventory[i.key] !== savedInventory[i.key]);

  // Purchase Log state
  const [purchaseType, setPurchaseType] = useState(null);
  const [purchaseQty, setPurchaseQty] = useState(0);
  const [purchaseCost, setPurchaseCost] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(todayISO());
  const [purchaseSaving, setPurchaseSaving] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [purchases, setPurchases] = useState([]);

  // Collapsible sections (default collapsed)
  const [showInventory, setShowInventory] = useState(false);
  const [showPurchaseLog, setShowPurchaseLog] = useState(false);

  // Admin check — only show inventory/purchases for parents
  const userEmail = (accounts[0]?.username || '').toLowerCase();
  const isMarc = userEmail === MARC_EMAIL;

  // Date context
  const today = todayISO();
  const isPast = date < today;
  const isFuture = date > today;
  const isToday = date === today;
  const readOnly = isPast && userEmail !== 'marc@defineholy.com';

  function formatDateHeader(isoDate) {
    const d = new Date(isoDate + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  }

  const calculatedHeadcount = members.filter(m => m.checked).length + guests.length;
  const headcount = headcountOverride != null ? headcountOverride : calculatedHeadcount;

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  // Build protein orders JSON from current state
  function buildProteinOrders() {
    const orders = {};
    members.filter(m => m.checked).forEach(m => {
      orders[m.name] = m.protein || 'Steak';
    });
    guests.forEach(g => {
      const name = g.name.trim() || `Guest ${guests.indexOf(g) + 1}`;
      orders[name] = g.protein || 'Steak';
    });
    return JSON.stringify(orders);
  }

  // Parse protein field — backward compatible
  function parseProteinField(value) {
    if (!value) return null;
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) return parsed;
    } catch { /* not JSON */ }
    return null; // old format string like "Steak"
  }

  // Load existing order for selected date
  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const items = await getItems(token, LIST_NAME);
      const match = items.find(item => item.OrderDate === date);

      if (match) {
        setExistingId(match.id);

        const guestNames = match.GuestNames ? match.GuestNames.split(',').map(s => s.trim()).filter(Boolean) : [];
        setDinnerTime(match.DinnerTime || '5:00 PM');
        setHeadcountOverride(match.Headcount != null ? match.Headcount : null);

        // Parse protein orders
        const proteinOrders = parseProteinField(match.Protein);

        if (proteinOrders) {
          // JSON format — restore per-person selections
          setMembers(FAMILY.map(fm => ({
            ...fm,
            checked: true,
            protein: proteinOrders[fm.name] || 'Steak',
          })));
          setGuests(guestNames.map(n => ({
            name: n,
            protein: proteinOrders[n] || 'Steak',
          })));
        } else {
          // Old format — everyone gets the same protein
          const defaultProtein = match.Protein || 'Steak';
          setMembers(FAMILY.map(fm => ({ ...fm, checked: true, protein: defaultProtein })));
          setGuests(guestNames.map(n => ({ name: n, protein: defaultProtein })));
        }
      } else {
        setExistingId(null);
        setMembers(FAMILY.map(fm => ({ ...fm, checked: fm.default, protein: 'Steak' })));
        setGuests([]);
        setDinnerTime('5:00 PM');
        setHeadcountOverride(null);
      }
      // Load kitchen inventory
      try {
        const invItems = await getItems(token, INV_LIST);
        const counts = Object.fromEntries(INV_ITEMS.map(i => [i.key, 0]));
        const ids = {};
        invItems.forEach(it => {
          let type = it.ItemType || it.Title || '';
          // Map old names to current keys
          if (INV_TYPE_ALIASES[type]) type = INV_TYPE_ALIASES[type];
          if (counts.hasOwnProperty(type)) {
            counts[type] = it.Quantity || 0;
            ids[type] = it.id;
          }
        });
        setInventory(counts);
        setSavedInventory(counts);
        setInvIds(ids);
      } catch { /* KitchenInventory may not exist yet */ }
      // Load purchase history
      try {
        const purchaseItems = await getItems(token, PURCHASE_LIST);
        const sorted = purchaseItems
          .map(p => ({
            id: p.id,
            date: p.PurchaseDate || '',
            itemType: p.ItemType || p.Title || '',
            quantity: p.Quantity || 0,
            cost: p.Cost || 0,
            notes: p.Notes || '',
          }))
          .sort((a, b) => b.date.localeCompare(a.date))
          .slice(0, 10);
        setPurchases(sorted);
      } catch { /* PurchaseLog may not exist yet */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, date]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  function toggleMember(index) {
    setHeadcountOverride(null);
    setMembers(prev => prev.map((m, i) => i === index ? { ...m, checked: !m.checked } : m));
  }

  function setMemberProtein(index, protein) {
    setMembers(prev => prev.map((m, i) => i === index ? { ...m, protein } : m));
  }

  function addGuest() {
    setHeadcountOverride(null);
    setGuests(prev => [...prev, { name: '', protein: 'Steak' }]);
  }

  function updateGuestName(index, name) {
    setGuests(prev => prev.map((g, i) => i === index ? { ...g, name } : g));
  }

  function setGuestProtein(index, protein) {
    setGuests(prev => prev.map((g, i) => i === index ? { ...g, protein } : g));
  }

  function removeGuest(index) {
    setHeadcountOverride(null);
    setGuests(prev => prev.filter((_, i) => i !== index));
  }

  function adjustInventory(delta) {
    if (!selectedInv) return;
    setInventory(prev => ({
      ...prev,
      [selectedInv]: Math.max(0, prev[selectedInv] + delta),
    }));
  }

  async function handleInvSave() {
    setInvSaving(true);
    setInvSuccess(null);
    try {
      const token = await getToken();
      const now = new Date().toISOString();
      for (const item of INV_ITEMS) {
        if (inventory[item.key] !== savedInventory[item.key]) {
          if (invIds[item.key]) {
            await updateItem(token, INV_LIST, invIds[item.key], {
              Quantity: inventory[item.key],
              LastUpdated: now,
            });
          } else {
            const created = await createItem(token, INV_LIST, {
              Title: item.key,
              ItemType: item.key,
              Quantity: inventory[item.key],
              LastUpdated: now,
            });
            setInvIds(prev => ({ ...prev, [item.key]: created.id }));
          }
        }
      }
      setSavedInventory({ ...inventory });
      setInvSuccess('Inventory updated');
      setTimeout(() => setInvSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setInvSaving(false);
    }
  }

  async function handleLogPurchase() {
    if (!purchaseType || purchaseQty <= 0 || !purchaseCost) return;
    setPurchaseSaving(true);
    setPurchaseSuccess(null);
    try {
      const token = await getToken();
      const costNum = parseFloat(purchaseCost);
      if (isNaN(costNum)) throw new Error('Invalid cost');
      await createItem(token, PURCHASE_LIST, {
        Title: `${purchaseType} x${purchaseQty}`,
        PurchaseDate: purchaseDate,
        ItemType: purchaseType,
        Quantity: purchaseQty,
        Cost: costNum,
        Notes: '',
      });
      setPurchases(prev => [{
        id: Date.now().toString(),
        date: purchaseDate,
        itemType: purchaseType,
        quantity: purchaseQty,
        cost: costNum,
        notes: '',
      }, ...prev].slice(0, 10));
      setPurchaseQty(0);
      setPurchaseCost('');
      setPurchaseSuccess('Purchase logged');
      setTimeout(() => setPurchaseSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setPurchaseSaving(false);
    }
  }

  function getAvgCost(itemType) {
    const matching = purchases.filter(p => p.itemType === itemType && p.cost > 0 && p.quantity > 0);
    if (matching.length === 0) return null;
    const totalCost = matching.reduce((s, p) => s + p.cost, 0);
    const totalQty = matching.reduce((s, p) => s + p.quantity, 0);
    return totalCost / totalQty;
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const guestNames = guests.map(g => g.name.trim()).filter(Boolean).join(', ');
      const fields = {
        Title: `Dinner ${date}`,
        OrderDate: date,
        Headcount: headcount,
        GuestNames: guestNames,
        DinnerTime: dinnerTime,
        Protein: buildProteinOrders(),
      };

      if (existingId) {
        await updateItem(token, LIST_NAME, existingId, fields);
        setSuccess('Plan updated.');
      } else {
        await createItem(token, LIST_NAME, fields);
        setSuccess('Plan saved.');
        await loadOrder();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="DinnerOrder"><p className="DinnerOrder-loading">Loading dinner plan...</p></div>;
  }

  return (
    <div className={`DinnerOrder${isPast ? ' DinnerOrder--past' : ''}${isFuture ? ' DinnerOrder--future' : ''}`}>
      {/* Date header with context */}
      <h1 className={`DinnerOrder-title${isPast ? ' DinnerOrder-title--past' : ''}${isFuture ? ' DinnerOrder-title--future' : ''}`}>
        {isToday && (existingId ? "Tonight's Dinner" : 'Dinner Plan')}
        {isPast && 'Dinner Plan'}
        {isFuture && 'Dinner Plan'}
        {' — '}
        {formatDateHeader(date)}
        {isPast && <span className="DinnerOrder-date-tag"> (Past)</span>}
        {isFuture && <span className="DinnerOrder-date-tag"> (Upcoming)</span>}
      </h1>

      {/* Back to Tonight button */}
      {!isToday && (
        <button
          type="button"
          className="DinnerOrder-back-btn"
          onClick={() => setDate(today)}
        >
          Tonight's Dinner
        </button>
      )}

      {isFuture && (
        <p className="DinnerOrder-future-note">Planning ahead — this is not tonight's dinner</p>
      )}

      {error && <p className="DinnerOrder-error">{error}</p>}
      {success && <p className="DinnerOrder-success">{success}</p>}

      <form className="DinnerOrder-form" onSubmit={handleSave}>
        {/* Date picker (secondary) */}
        <label className="DinnerOrder-label" style={{ fontSize: '0.8rem' }}>Change Date</label>
        <input
          className="DinnerOrder-input"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{ fontSize: '0.9rem' }}
        />

        {/* Family Members with per-person protein */}
        <label className="DinnerOrder-label">Who's Eating</label>
        <div className="DinnerOrder-orders">
          {members.map((m, i) => (
            <div key={m.name} className="DinnerOrder-order-row">
              <label className="DinnerOrder-member">
                <input
                  type="checkbox"
                  checked={m.checked}
                  onChange={() => toggleMember(i)}
                  disabled={readOnly}
                />
                <span>{m.name}</span>
              </label>
              {m.checked && (
                <select
                  className="DinnerOrder-protein-select"
                  value={m.protein}
                  onChange={e => setMemberProtein(i, e.target.value)}
                  disabled={readOnly}
                >
                  {PROTEIN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>

        {/* Guests */}
        <label className="DinnerOrder-label">
          Guests
          {!readOnly && (
            <button type="button" className="DinnerOrder-add-guest" onClick={addGuest}>
              + Add Guest
            </button>
          )}
        </label>
        {guests.map((g, i) => (
          <div key={i} className="DinnerOrder-guest-row">
            <input
              className="DinnerOrder-input DinnerOrder-guest-input"
              type="text"
              placeholder="Guest name (optional)"
              value={g.name}
              onChange={e => updateGuestName(i, e.target.value)}
              disabled={readOnly}
            />
            <select
              className="DinnerOrder-protein-select"
              value={g.protein}
              onChange={e => setGuestProtein(i, e.target.value)}
              disabled={readOnly}
            >
              {PROTEIN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            {!readOnly && (
              <button
                type="button"
                className="DinnerOrder-remove-guest"
                onClick={() => removeGuest(i)}
              >
                &times;
              </button>
            )}
          </div>
        ))}

        {/* Headcount */}
        <div className="DinnerOrder-headcount">
          Headcount: <strong>{headcount}</strong>
        </div>

        {/* Dinner Time */}
        <label className="DinnerOrder-label">Dinner Time</label>
        <input
          className="DinnerOrder-input"
          type="text"
          value={dinnerTime}
          onChange={e => setDinnerTime(e.target.value)}
          disabled={readOnly}
        />

        {/* Save — hidden when read-only */}
        {!readOnly && (
          <button className="DinnerOrder-save-btn" type="submit" disabled={saving}>
            {saving ? 'Saving...' : existingId ? 'Update Plan' : 'Save Plan'}
          </button>
        )}
      </form>

      {/* Kitchen Inventory — admin only, collapsible */}
      {isMarc && (
        <>
          <h2
            className="DinnerOrder-title DinnerOrder-title--collapsible"
            style={{ marginTop: '2rem' }}
            onClick={() => setShowInventory(!showInventory)}
          >
            Kitchen Inventory <span className="DinnerOrder-collapse-icon">{showInventory ? '−' : '+'}</span>
          </h2>

          {showInventory && (
            <>
              <div className="Inv-rows">
                {INV_ITEMS.map(item => (
                  <button
                    key={item.key}
                    type="button"
                    className={`Inv-row${selectedInv === item.key ? ' Inv-row--selected' : ''}`}
                    onClick={() => setSelectedInv(selectedInv === item.key ? null : item.key)}
                  >
                    <span className="Inv-row-label">{item.label}</span>
                    <span className="Inv-row-count">{inventory[item.key]}{item.unit ? ` ${item.unit}` : ''}</span>
                    <span className="Inv-row-equiv">{item.steakInfo(inventory[item.key])}</span>
                  </button>
                ))}
              </div>

              <div className="Inv-arrows">
                <button type="button" className="Inv-arrow Inv-arrow--up" onClick={() => adjustInventory(1)} disabled={!selectedInv}>&#9650;</button>
                <button type="button" className="Inv-arrow Inv-arrow--down" onClick={() => adjustInventory(-1)} disabled={!selectedInv || inventory[selectedInv] <= 0}>&#9660;</button>
              </div>

              {invSuccess && <p className="DinnerOrder-success">{invSuccess}</p>}

              <button
                type="button"
                className={`Inv-save-btn${invDirty ? ' Inv-save-btn--dirty' : ''}`}
                onClick={handleInvSave}
                disabled={!invDirty || invSaving}
              >
                {invSaving ? 'Saving...' : invDirty ? 'Update Inventory' : 'Inventory Current'}
              </button>
            </>
          )}
        </>
      )}

      {/* Purchase Log — admin only, collapsible */}
      {isMarc && (
        <>
          <h2
            className="DinnerOrder-title DinnerOrder-title--collapsible"
            style={{ marginTop: '2rem' }}
            onClick={() => setShowPurchaseLog(!showPurchaseLog)}
          >
            Purchase Log <span className="DinnerOrder-collapse-icon">{showPurchaseLog ? '−' : '+'}</span>
          </h2>

          {showPurchaseLog && (
            <>
              <div className="Inv-rows">
                {PURCHASE_TYPES.map(type => (
                  <button
                    key={type}
                    type="button"
                    className={`Inv-row${purchaseType === type ? ' Inv-row--selected' : ''}`}
                    onClick={() => setPurchaseType(purchaseType === type ? null : type)}
                  >
                    <span className="Inv-row-label">{type}</span>
                    {getAvgCost(type) != null && (
                      <span className="Inv-row-equiv">Avg: ${getAvgCost(type).toFixed(0)}/ea</span>
                    )}
                  </button>
                ))}
              </div>

              {purchaseType && (
                <div className="Purchase-form">
                  <div className="Purchase-field-row">
                    <label className="DinnerOrder-label">Qty{(purchaseType === 'Ground Beef' || purchaseType === 'Wings') ? ' (lbs)' : ''}</label>
                    <div className="Inv-arrows" style={{ margin: 0 }}>
                      <button type="button" className="Inv-arrow Inv-arrow--up" style={{ width: 48, height: 48, fontSize: '1.3rem' }} onClick={() => setPurchaseQty(q => q + 1)}>&#9650;</button>
                      <span className="Inv-row-count" style={{ minWidth: '2.5rem', textAlign: 'center' }}>{purchaseQty}{(purchaseType === 'Ground Beef' || purchaseType === 'Wings') ? ' lbs' : ''}</span>
                      <button type="button" className="Inv-arrow Inv-arrow--down" style={{ width: 48, height: 48, fontSize: '1.3rem' }} onClick={() => setPurchaseQty(q => Math.max(0, q - 1))} disabled={purchaseQty <= 0}>&#9660;</button>
                    </div>
                  </div>
                  <div className="Purchase-field-row">
                    <label className="DinnerOrder-label">Total Cost</label>
                    <div className="Purchase-cost-wrap">
                      <span className="Purchase-cost-prefix">$</span>
                      <input
                        className="DinnerOrder-input Purchase-cost-input"
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={purchaseCost}
                        onChange={e => {
                          const v = e.target.value.replace(/[^0-9.]/g, '');
                          setPurchaseCost(v);
                        }}
                      />
                    </div>
                  </div>
                  <div className="Purchase-field-row">
                    <label className="DinnerOrder-label">Date</label>
                    <input
                      className="DinnerOrder-input"
                      type="date"
                      value={purchaseDate}
                      onChange={e => setPurchaseDate(e.target.value)}
                    />
                  </div>
                  {purchaseQty > 0 && purchaseCost && (
                    <div className="Purchase-per-unit">
                      ${(parseFloat(purchaseCost) / purchaseQty).toFixed(2)} per {purchaseType}
                    </div>
                  )}
                </div>
              )}

              {purchaseSuccess && <p className="DinnerOrder-success">{purchaseSuccess}</p>}

              <button
                type="button"
                className={`Inv-save-btn${purchaseType && purchaseQty > 0 && purchaseCost ? ' Inv-save-btn--dirty' : ''}`}
                onClick={handleLogPurchase}
                disabled={!purchaseType || purchaseQty <= 0 || !purchaseCost || purchaseSaving}
              >
                {purchaseSaving ? 'Saving...' : 'Log Purchase'}
              </button>

              {/* Purchase History */}
              {purchases.length > 0 && (
                <>
                  <h3 className="Purchase-history-title">Recent Purchases</h3>
                  <div className="Purchase-history">
                    {purchases.map(p => (
                      <div key={p.id} className="Purchase-history-row">
                        <span className="Purchase-history-date">{p.date}</span>
                        <span className="Purchase-history-item">{p.itemType}</span>
                        <span className="Purchase-history-qty">×{p.quantity}</span>
                        <span className="Purchase-history-cost">${p.cost.toFixed(0)}</span>
                        <span className="Purchase-history-unit">${(p.cost / p.quantity).toFixed(0)}/ea</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default DinnerOrder;
